import { Injectable } from '@angular/core';
import * as THREE from '@phdsoft/three';
import { OcclusionCuller } from './occlusion-culler';
import { CullingConfig, CullingStats } from './bvh.types';
import { CULL_PROXY_NAME, CullSection, CullableSectionSource, createCullSection, isAttachedToScene } from './cull-section';

// was: a local hasSceneAncestor(object) walk — now the shared isAttachedToScene() in cull-section.ts

@Injectable({ providedIn: 'root' })
export class CullingService {
  private culler: OcclusionCuller;
  private _isReady: boolean = false;
  private _enabled: boolean = true;
  private allMeshes: THREE.Mesh[] = [];
  // was: private lastBuildRoot: THREE.Object3D | null = null; — sections now come from a provider (asset regions)
  private lastSources: CullableSectionSource[] | null = null;
  private lastIsNewModelMode: boolean = false;
  private sectionProvider: (() => CullableSectionSource[]) | null = null;
  private newModelModeProvider: (() => boolean) | null = null;
  private rebuildsDeferred = false;

  constructor() {
    this.culler = new OcclusionCuller();
  }

  get isReady(): boolean {
    return this._isReady;
  }

  get enabled(): boolean {
    return this._enabled;
  }

  set enabled(v: boolean) {
    this._enabled = v;
    // When disabling, show every section the culler hid (it only writes proxy.visible).
    if (!v) {
      // was: for (const mesh of this.allMeshes) { mesh.visible = true; } — the culler no longer writes mesh flags
      this.culler.restoreAll();
    }
  }

  /**
   * Register where rebuild() reads the sections from (AssetLoader: every section of
   * every structure region). `isNewModelMode` lets a rebuild that runs before the
   * first build (region loads during loading) know the model mode; without it,
   * rebuild() reuses the mode of the last build.
   */
  setSectionProvider(provider: () => CullableSectionSource[], isNewModelMode?: () => boolean): void {
    this.sectionProvider = provider;
    this.newModelModeProvider = isNewModelMode ?? null;
  }

  /**
   * Wrap each source's LightSection under a culler-owned '__cullProxy'
   * group and hand the sections to the culler. Every proxy the culler hid before is
   * shown first. Old-format models (isNewModelMode false) are not culled.
   */
  build(sources: CullableSectionSource[], isNewModelMode: boolean = false): void {
    this._isReady = false;
    this.lastSources = sources;
    this.lastIsNewModelMode = isNewModelMode;
    this.allMeshes = [];
    // Show every proxy of the previous build before the section list changes.
    this.culler.restoreAll();
    if (!isNewModelMode) {
      // Old format: skip culling and ensure stale state is cleared
      this.culler.clear();
      return;
    }
    // was: root.traverse(...) via collectSectionsWithoutProxy(root) — sections had no proxy and were never culled
    const sections: CullSection[] = [];
    // A group listed twice would give a second CullSection sharing the same plates and proxy; it
    // is tested after its twin was rasterized, ties with its own plates under the strict occludee
    // margin and hides the shared proxy. Wrap each group once per build.
    const wrappedGroups = new Set<THREE.Object3D>();
    for (const source of sources ?? []) {
      if (source?.group && wrappedGroups.has(source.group)) {
        continue;
      }
      const section = this.wrapSection(source);
      if (section) {
        wrappedGroups.add(source.group);
        sections.push(section);
        this.allMeshes.push(...section.plates);
      }
    }
    this.culler.build(sections);
    this._isReady = true;
  }

  /**
   * Find or create the section's '__cullProxy' (identity transform, child of
   * source.group) and move LightSection under it; Borders stay a direct child of
   * source.group (moved back out if an earlier wrap put them in the proxy). Idempotent. Returns
   * null (section not wrapped, not culled) when the group is detached from the
   * scene, there is no LightSection under the group, or it has no SurfaceTypesMesh.
   */
  private wrapSection(source: CullableSectionSource): CullSection | null {
    const group = source?.group;
    const lightSectionGroup = source?.lightSectionGroup;
    if (!group || !lightSectionGroup || !isAttachedToScene(group)) {
      return null;
    }
    const section = createCullSection(source, null);
    if (section.plates.length === 0) {
      return null;
    }

    const existing = group.children.find(child => child.name === CULL_PROXY_NAME);
    const existingProxy = existing instanceof THREE.Group ? existing : null;
    if (lightSectionGroup.parent !== group && (!existingProxy || lightSectionGroup.parent !== existingProxy)) {
      return null; // LightSection is not where the loader puts it: hiding a proxy would not hide it
    }

    let proxy = existingProxy;
    if (!proxy) {
      proxy = new THREE.Group();
      proxy.name = CULL_PROXY_NAME;
      group.add(proxy);
    }
    // Identity proxy: reparenting keeps every world transform.
    if (lightSectionGroup.parent === group) {
      proxy.add(lightSectionGroup);
    }
    // was: const borderGroup = source.borderGroup;
    // was: if (borderGroup && borderGroup.parent === group) { proxy.add(borderGroup); }
    // identity proxy: same world transform; Borders are depth-tested edge lines and keep drawing while the section is culled (T-joint line)
    if (source.borderGroup?.parent === proxy) {
      group.add(source.borderGroup);
    }
    // was: group.updateMatrixWorld(true); — redundant (identity proxy) and it updated the whole section
    // subtree; computeSectionLocalBox (culler build) updates only LightSection.

    section.proxy = proxy;
    return section;
  }

  // TEMPORARY bridge from Task 2, replaced by build(sources) + wrapSection (section proxies):
  // private collectSectionsWithoutProxy(root: THREE.Object3D): CullSection[] {
  //   const sections: CullSection[] = [];
  //   root.traverse(obj => {
  //     const lightSectionGroup = obj.children.find(child => child.name?.includes('LightSection'));
  //     if (!lightSectionGroup) {
  //       return;
  //     }
  //     const source: CullableSectionSource = {
  //       group: obj,
  //       lightSectionGroup,
  //       borderGroup: obj.children.find(child => child.name?.includes('Borders'))
  //     };
  //     const section = createCullSection(source, null);
  //     if (section.plates.length > 0) {
  //       sections.push(section);
  //     }
  //   });
  //   return sections;
  // }

  cull(camera: THREE.Camera): void {
    if (!this._isReady || !this._enabled) {
      return;
    }
    this.culler.cull(camera);
  }

  /**
   * For idle safety renders: cull only if the camera, mesh visibility or
   * occluder materials changed since the last cull (catches app changes that
   * never call requestRender, e.g. view-mode material swaps, hidden regions).
   */
  cullIfChanged(camera: THREE.Camera): void {
    if (!this._isReady || !this._enabled) {
      return;
    }
    this.culler.cullIfChanged(camera);
  }

  /**
   * While true, rebuild() is a no-op. AssetLoader sets it around the initial structure
   * load so the per-region rebuilds do not re-wrap / re-measure every section loaded so
   * far (regions² × sections); loadAsset's final rebuild() wraps them all at once.
   * Region loads after the asset is loaded (deferral off) still rebuild immediately.
   */
  deferRebuilds(defer: boolean): void {
    this.rebuildsDeferred = defer;
  }

  /** Re-read the sections (provider, else the last build's sources) and build again. */
  rebuild(): void {
    if (this.rebuildsDeferred) {
      return;
    }
    // was: if (this.lastBuildRoot) { this.build(this.lastBuildRoot, this.lastIsNewModelMode); }
    const isNewModelMode = this.newModelModeProvider ? this.newModelModeProvider() : this.lastIsNewModelMode;
    if (this.sectionProvider) {
      this.build(this.sectionProvider(), isNewModelMode);
    } else if (this.lastSources) {
      this.build(this.lastSources, isNewModelMode);
    }
  }

  getStats(): CullingStats {
    return this.culler.stats;
  }

  getConfig(): CullingConfig {
    return { ...this.culler.config };
  }

  setConfig(config: Partial<CullingConfig>): void {
    Object.assign(this.culler.config, config);
    this.culler.invalidateSignature();
  }

  /** Show every proxy, then drop the sections and the provider (proxies stay in the graph, visible). */
  clear(): void {
    this.culler.clear(); // restoreAll() first, then drops the sections
    this._isReady = false;
    this.allMeshes = [];
    // was: this.lastBuildRoot = null;
    this.lastSources = null;
    this.lastIsNewModelMode = false;
    this.sectionProvider = null;
    this.newModelModeProvider = null;
  }

  getAllPickableObjects(): THREE.Mesh[] {
    return this.allMeshes;
  }
}
